import React from 'react';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { Badge, Table } from 'reactstrap';
import { actions } from 'modules/checkout';
import {
  getCurrentOfferPaymentOption,
  getCurrentTotal,
  getStandaloneTotal,
  orderRequiresShipping
} from 'modules/checkout/selectors';
import formatCurrency from 'lib/formatCurrency';

const OrderSummaryProduct = ({ img, name, qty, price, isPaymentPlan, Discount, ...rest }) => (
  <React.Fragment>
    <tr {...rest}>
      <td style={{ position: 'relative' }}>
        <img src={`${process.env.USER_CONTENT_URL}${img}`} style={{ maxWidth: '50px' }} />
      </td>
      <td>{name}</td>
      <td className="text-right">{formatCurrency.format(Number(qty) * Number(price))} (USD)</td>
    </tr>
    {Discount && !isPaymentPlan ? (
      <React.Fragment>
        <tr {...rest}>
          <td></td>
          <td colSpan={2} className="text-right">
            <strong>(with {formatCurrency.format(Number(Discount))} deposit (USD) applied)</strong>
          </td>
        </tr>
      </React.Fragment>
    ) : null}
  </React.Fragment>
);

const RecurringChargeSection = ({ trialCount, trialInterval, interval, total, pending, loadingProps }) => (
  <React.Fragment>
    <tr>
      <td colSpan={3}>
        * After your {trialCount}-{trialInterval} trial, you will be charged the following every {interval}:
      </td>
    </tr>
    <tr>
      <td colSpan={2} style={{ borderTop: '1px solid' }}>
        Subtotal
      </td>
      <td style={{ borderTop: '1px solid' }} className="text-right">
        {formatCurrency.format(Number(total.order.firstPaymentAmount) - Number(total.order.firstPaymentTax))} (USD)
      </td>
    </tr>
    <tr>
      <td colSpan={2}>Sales tax</td>
      <td className={`text-right`}>
        {pending ? <PulseLoader {...loadingProps} /> : `\$${total.order.firstPaymentTax} (USD)`}
      </td>
    </tr>
    <tr>
      <td colSpan={2} style={{ borderTop: '1px solid' }}>
        <strong>Total</strong>
      </td>
      <td style={{ borderTop: '1px solid' }} className={`text-right`}>
        <strong>
          {pending ? (
            <PulseLoader {...loadingProps} />
          ) : (
            `${formatCurrency.format(Number(total.order.firstPaymentAmount))} (USD)`
          )}
        </strong>
      </td>
    </tr>
  </React.Fragment>
);

const OrderSummary = props => {
  const {
    offer,
    orderRequest,
    orderRequiresShipping,
    processStandaloneBumpOffer,
    standaloneBumpOffer,
    standaloneTotal,
    total,
    totals: { pending }
  } = props;

  const bumpOffers = props.currentOfferPaymentOption ? props.currentOfferPaymentOption.BumpOffers : [];

  const standaloneOffer =
    processStandaloneBumpOffer && standaloneBumpOffer && standaloneBumpOffer.Offer ? standaloneBumpOffer.Offer : null;
  const standaloneOfferPaymentOption = standaloneOffer
    ? standaloneOffer.OfferPaymentOptions.find(opo => opo.id === standaloneBumpOffer.OfferPaymentOptionId)
    : null;
  const standaloneBumpOffers = standaloneOfferPaymentOption ? [standaloneOfferPaymentOption] : [];

  const { trialCount, trialInterval, interval } = props.currentOfferPaymentOption || {};
  const standaloneHasTrial = standaloneOfferPaymentOption && standaloneOfferPaymentOption.trialCount;
  const standaloneTax = standaloneTotal && !standaloneHasTrial ? Number(standaloneTotal.order.tax) : 0;
  const standaloneSubtotal = standaloneTotal && !standaloneHasTrial ? Number(standaloneTotal.order.subtotal) : 0;
  const orderProductTotal = total && total.orderProducts.filter(p => p.ProductId === offer.Product.id)[0];
  const loadingProps = { size: '8px', color: '#212529' };
  const isPaymentPlan = total && total.order.numInstallments > 1;
  return total ? (
    <Table borderless>
      <tbody>
        <OrderSummaryProduct
          img={offer.image}
          name={offer.Product.name}
          qty={orderProductTotal.qtyOrdered}
          price={orderProductTotal.price}
          isPaymentPlan={isPaymentPlan}
          Discount={offer.Discount}
        />
        {bumpOffers
          .filter(op => !op.Product.slug.match(/gmo/i) && !offer.Product.slug.match(/pbcc/i))
          .map(bo => {
            const orderProductTotal = total.orderProducts.filter(p => p.BumpOfferId === bo.id)[0];
            return orderProductTotal ? (
              <OrderSummaryProduct
                img={bo.image}
                name={bo.name}
                qty={orderProductTotal.qtyOrdered}
                price={orderProductTotal.price}
                className={Number(orderProductTotal.price) === 0 ? 'free-bumpOffer' : null}
              />
            ) : null;
          })}
        {standaloneBumpOffers.map(bo => {
          // implicit assumption qty is 1
          return (
            <OrderSummaryProduct
              img={standaloneOffer.image}
              name={standaloneOffer.name}
              qty={1}
              price={bo.trialCount ? bo.trialPrice : bo.amount}
            />
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2} style={{ borderTop: '1px solid' }}>
            Subtotal
          </td>
          <td style={{ borderTop: '1px solid' }} className="text-right">
            {formatCurrency.format(Number(total.order.subtotal) + standaloneSubtotal)} (USD)
          </td>
        </tr>
        {orderRequiresShipping || (isNaN(total.order.shipping) && Number(total.order.shipping) > 0) ? (
          <tr>
            <td colSpan={2}>Shipping</td>
            <td className={`text-right`}>
              {pending ? <PulseLoader {...loadingProps} /> : `\$${total.order.shipping} (USD)`}
            </td>
          </tr>
        ) : null}
        <tr>
          <td colSpan={2}>Sales tax</td>
          <td className={`text-right`}>
            {pending ? (
              <PulseLoader {...loadingProps} />
            ) : (
              `${formatCurrency.format(Number(total.order.tax) + standaloneTax)} (USD)`
            )}
          </td>
        </tr>
        <tr>
          <td colSpan={2} style={{ borderTop: '1px solid' }}>
            <span style={!isPaymentPlan ? { fontWeight: 'bolder' } : {}}>Order total</span>
          </td>
          <td style={{ borderTop: '1px solid' }} className={`text-right`}>
            <span style={!isPaymentPlan ? { fontWeight: 'bolder' } : {}}>
              {pending ? (
                <PulseLoader {...loadingProps} />
              ) : (
                `${formatCurrency.format(Number(total.order.subtotal) + Number(total.order.tax) + standaloneSubtotal + standaloneTax)} (USD)`
              )}
            </span>
          </td>
        </tr>
        {isPaymentPlan ? (
          <React.Fragment>
            <tr>
              <td colSpan={2} style={{ borderTop: '1px solid' }}>
                <strong>Today's Payment</strong>
              </td>
              <td style={{ borderTop: '1px solid' }} className={`text-right`}>
                <strong>
                  {pending ? (
                    <PulseLoader {...loadingProps} />
                  ) : (
                    `${formatCurrency.format(Number(total.order.firstPaymentAmount))} (USD)`
                  )}
                </strong>
              </td>
            </tr>
            {offer.Discount ? (
              <tr>
                <td colSpan={3}>
                  <strong>(with {formatCurrency.format(Number(offer.Discount))} deposit (USD) applied)</strong>
                </td>
              </tr>
            ) : null}
          </React.Fragment>
        ) : null}
        {trialCount ? (
          <RecurringChargeSection
            trialCount={trialCount}
            trialInterval={trialInterval}
            interval={interval}
            total={total}
            pending={pending}
            loadingProps={loadingProps}
          />
        ) : null}
        {standaloneHasTrial && standaloneTotal ? (
          <RecurringChargeSection
            trialCount={standaloneOfferPaymentOption.trialCount}
            trialInterval={standaloneOfferPaymentOption.trialInterval}
            interval={standaloneOfferPaymentOption.interval}
            total={standaloneTotal}
            pending={pending}
            loadingProps={loadingProps}
          />
        ) : null}
      </tfoot>
    </Table>
  ) : null;
};

OrderSummary.propTypes = {
  offer: PropTypes.shape({}).isRequired,
  orderRequest: PropTypes.shape({}).isRequired,
  totals: PropTypes.shape({}).isRequired
};

const mapStateToProps = state => ({
  currentOfferPaymentOption: getCurrentOfferPaymentOption(state),
  offer: state.checkout.offer,
  orderRequest: state.checkout.orderRequest,
  orderRequiresShipping: orderRequiresShipping(state),
  processStandaloneBumpOffer: state.checkout.processStandaloneBumpOffer,
  standaloneBumpOffer: state.checkout.standaloneBumpOffer,
  standaloneTotal: getStandaloneTotal(state),
  total: getCurrentTotal(state),
  totals: state.checkout.totals
});

export default connect(mapStateToProps, null)(OrderSummary);
